import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { FindAllPaymentsResponseDTO, PaymentCreateRequestDTO, PaymentDTO, PaymentFilters } from "./dto";
import { NewPaymentService } from "./service";
import { PromiseStatuses } from "../../../lib/utilis";

interface PaymentState {
    findAllPaymentResponse?: FindAllPaymentsResponseDTO
    findAllPaymentStatus: PromiseStatuses
    findAllFilters: PaymentFilters
    findPaymentByIdResponse?: PaymentDTO
    findPaymentByIdStatus: PromiseStatuses
    createPaymentRequest: PaymentCreateRequestDTO
    createPaymentStatus: PromiseStatuses
    createPaymentWithIdStatus: PromiseStatuses
    deletePaymentStatus: PromiseStatuses
}

const initialState: PaymentState = {
    createPaymentRequest: {
        amount: 0,
        customer_id: '',
        markup_rate_applied: 0,
        stripe_payment_method: '',
        stripe_payment_intent: "",
    },
    findAllPaymentStatus: "idle",
    findPaymentByIdStatus: "idle",
    createPaymentStatus: "idle",
    deletePaymentStatus: "idle",
    findAllFilters: {
        page: 0,
        pageSize: 50,
        sort: 0,
        sortBy: '',
        customerId: ''
    },
    createPaymentWithIdStatus: "idle"
}

export const findAllPayments = createAsyncThunk(
    'payment/findAllPayments',
    async (filters: PaymentFilters, thunkApi): Promise<FindAllPaymentsResponseDTO> => {
        const paymentService = NewPaymentService()

        return paymentService.findAllPayments(filters)
    }
)

export const findPaymentById = createAsyncThunk(
    'payment/findPaymentById',
    async (id: string, thunkApi): Promise<PaymentDTO> => {
        const paymentService = NewPaymentService()

        return paymentService.findPaymentById(id)
    }
)

export const createPayment = createAsyncThunk(
    'payment/createPayment',
    async (request: PaymentDTO, thunkApi): Promise<void> => {
        const paymentService = NewPaymentService()

        return paymentService.createPayment(request)
    }
)

export const createPaymentWithId = createAsyncThunk(
    'payment/createPaymentWithId',
    async (id: string, thunkApi): Promise<void> => {
        const paymentService = NewPaymentService()

        return paymentService.createPaymentWithId(id)
    }
)

export const deletePayment = createAsyncThunk(
    'payment/deletePayment',
    async (id: string, thunkApi): Promise<void> => {
        const paymentService = NewPaymentService()

        return paymentService.deletePayment(id)
    }
)

const paymentSlice = createSlice(
    {
        name: 'payment/slice',
        initialState,
        reducers: {
            resetCreatePaymentRequest: (state, action) => {
                state.createPaymentRequest.amount = 0
                state.createPaymentRequest.customer_id = ''
                state.createPaymentRequest.markup_rate_applied = 0
            },
            setFindAllPaymentStatus: (state, action) => {
                state.findAllPaymentStatus = action.payload
            },
            setFindPaymentByIdStatus: (state, action) => {
                state.findPaymentByIdStatus = action.payload
            },
            setCreatePaymentStatus: (state,action) => {
                state.createPaymentWithIdStatus = action.payload
            }
        },
        extraReducers(builder) {
            builder.addCase(findAllPayments.fulfilled, (state, action) => {
                state.findAllPaymentStatus = 'successfully'
                state.findAllPaymentResponse = action.payload
            }).addCase(findAllPayments.pending, (state) => {
                state.findAllPaymentStatus = 'loading'
            }).addCase(findAllPayments.rejected, (state) => {
                state.findAllPaymentStatus = 'failed'
            }).addCase(findPaymentById.fulfilled, (state, action) => {
                state.findPaymentByIdStatus = 'successfully'
                state.findPaymentByIdStatus = 'successfully'
            }).addCase(findPaymentById.pending, (state) => {
                state.findPaymentByIdStatus = 'loading'
            }).addCase(findPaymentById.rejected, (state) => {
                state.findPaymentByIdStatus = 'failed'
            }).addCase(createPayment.fulfilled, (state) => {
                state.createPaymentStatus = 'successfully'
            }).addCase(createPayment.pending, (state) => {
                state.createPaymentStatus = 'loading'
            }).addCase(createPayment.rejected, (state) => {
                state.createPaymentStatus = 'failed'
            }).addCase(createPaymentWithId.fulfilled, (state) => {
                state.createPaymentWithIdStatus = 'successfully'
            }).addCase(createPaymentWithId.pending, (state) => {
                state.createPaymentWithIdStatus = 'loading'
            }).addCase(createPaymentWithId.rejected, (state) => {
                state.createPaymentWithIdStatus = 'failed'
            }).addCase(deletePayment.fulfilled, (state) => {
                state.deletePaymentStatus = 'successfully'
            }).addCase(deletePayment.pending, (state) => {
                state.deletePaymentStatus = 'loading'
            }).addCase(deletePayment.rejected, (state) => {
                state.deletePaymentStatus = 'failed'
            })
        },
    }
)

export default paymentSlice.reducer

export const { resetCreatePaymentRequest, setFindAllPaymentStatus, setFindPaymentByIdStatus, setCreatePaymentStatus } = paymentSlice.actions